import { useTranslations } from "next-intl";
import { useState } from "react";
import { useCurrentUser } from "@hooks/use-current-user";
import { useRestartSubscription, useCancelSuspension } from "@hooks/use-subscription";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import Image from "@components/core/Image";
import { showSuccessToast, showWarningToast } from "@utils/toasts/showToasts";
import { useReloadUser } from "@hooks/use-modify-user";
import { useReloadCustomizableCloset } from "@hooks/use-customizable-closet";

function SubscriptionPauseAlreadyAsked() {
  const t = useTranslations("accounts");

  const user = useCurrentUser();
  const reloadUser = useReloadUser();
  const reloadCustomizableCloset = useReloadCustomizableCloset();
  const restartSubscription = useRestartSubscription();
  const cancelSuspension = useCancelSuspension();

  const [loading, setLoading] = useState(false);

  function handleSuccessRestartByCreatingNewSubscription() {
    showSuccessToast(
      t("manage_subscription.pause.already_asked.restart_success.title"),
      t("manage_subscription.pause.already_asked.restart_success.description"),
    );
  }

  function handleErrorRestartByCreatingNewSubscription() {
    showWarningToast(
      t("manage_subscription.pause.already_asked.payment_failed.title"),
      t("manage_subscription.pause.already_asked.payment_failed.description"),
    );
  }

  function handleSuccessRestartByCancelingSuspension() {
    showSuccessToast(
      t("manage_subscription.cancelation.already_asked.suspension_request_canceled.title"),
      t(
        "manage_subscription.cancelation.already_asked.suspension_request_canceled.description",
      ),
    );
    setLoading(false);
  }

  function handleErrorRestartByCancelingSuspension() {
    showWarningToast(
      t(
        "manage_subscription.cancelation.already_asked.suspension_request_canceled_error.title",
      ),
      t(
        "manage_subscription.cancelation.already_asked.suspension_request_canceled_error.description",
      ),
    );
    setLoading(false);
  }

  async function restartByCreatingNewSubscription() {
    try {
      const subscription = await restartSubscription();

      if (["active", "trialing"].includes(subscription?.state)) {
        handleSuccessRestartByCreatingNewSubscription();
      } else {
        handleErrorRestartByCreatingNewSubscription();
      }
    } catch (err) {
      handleErrorRestartByCreatingNewSubscription();
    } finally {
      reloadUser();
      reloadCustomizableCloset();
    }
  }

  async function restartByCancelingSuspension() {
    try {
      await cancelSuspension();
      handleSuccessRestartByCancelingSuspension();
    } catch (err) {
      handleErrorRestartByCancelingSuspension();
    }
  }

  function restartNow() {
    if (loading) return;

    setLoading(true);

    if (user.subscription.state === "canceled") {
      restartByCreatingNewSubscription();
      return;
    }

    restartByCancelingSuspension();
  }

  return (
    <div
      data-testid="manage-subscription-pause-asked-section"
      className="manage-subscription-card"
    >
      <div className="manage-subscription-card-content">
        <div className="p-3 d-flex flex-column align-items-center">
          <h5 className="text-center mb-0">
            {t("manage_subscription.pause.already_asked.title")}
          </h5>

          <div
            className="my-5"
            style={{ width: "128px" }}
          >
            <Image
              src="handbagSleepingRetro"
              alt={t("manage_subscription.pause.alt")}
              className="w-100"
            />
          </div>

          <p className="text-center mb-0">
            {t("manage_subscription.pause.already_asked.description")}
          </p>
        </div>

        <div className="border-top">
          <div className="p-2 d-flex justify-content-center border-bottom">
            <Button
              variant="ghost-primary"
              onClick={restartNow}
              loading={loading}
            >
              {t("manage_subscription.pause.already_asked.restart_now")}
            </Button>
          </div>

          <div className="p-3 d-flex justify-content-center">
            <Link
              href="/profile/manage-subscription/pause/extend"
              className="link-primary"
              noUnderline
            >
              {t("manage_subscription.pause.already_asked.change_pause")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPauseAlreadyAsked;
